import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls';
import {
  setSelectedRequest,
  useCreateTransportMutation,
  EtlApiTransportRequestSchema,
  type EtlApiTransportRequest,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  trackSubmitAirAmbulanceRequest,
  trackSubmitAirAmbulanceRequestApiResponse,
} from '../../../../gainsightTrackings/airAmbulanceTrackings';
import { LAUNCH_TYPES } from '../../AirAmbulanceForm/constants';
import { getSelectValue } from '../../AirAmbulanceForm/utils';
import { parseCoordinates } from '../../AirAmbulanceForm/validation';
import type { FormValuesWithSelect } from '../../types';

interface AirAmbulanceEtlSubmitOptions {
  // TODO: isLoading/setIsLoading and UUID is here for compatibility with the
  //   legacy midas-based form submission handler signature. When the legacy
  //   form is removed, we can move the loading state management into this hook
  //   and remove these parameters.
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  UUID: string;
}

/**
 * Hook to handle Air Ambulance form submission to ETL service.
 *
 * @param options - Hook options including loading state, UUID, and PSAP info
 * @returns A function to submit form values to the ETL service
 */
function useAirAmbulanceEtlSubmit({
  isLoading,
  setIsLoading,
  UUID,
}: AirAmbulanceEtlSubmitOptions): (
  values: FormValuesWithSelect,
) => Promise<null | undefined> {
  const dispatch = useDispatch();
  const selectedCallerID = useSelectedCallerID();
  const [createTransport] = useCreateTransportMutation();

  return useCallback(
    async (values: FormValuesWithSelect) => {
      if (isLoading) return null;
      setIsLoading(true);

      trackSubmitAirAmbulanceRequest({
        requestId: selectedCallerID,
        requestType: values.launch_type,
        uuid: UUID,
        isEdit: false,
      });

      try {
        const payload = EtlApiTransportRequestSchema.parse(
          transformFormToApiPayload(values, selectedCallerID),
        );

        const result = await createTransport(payload).unwrap();
        setIsLoading(false);

        const gmrTransportId = result.gmr_transport_id;

        dispatch(setSelectedRequest(gmrTransportId));

        trackSubmitAirAmbulanceRequestApiResponse({
          requestId: gmrTransportId,
          status: 201,
          error: false,
          success: true,
          UUID,
          isEdit: false,
        });
      } catch (error) {
        const err = error as {
          response?: { status?: number };
          message?: string;
          status?: number;
        };

        trackSubmitAirAmbulanceRequestApiResponse({
          requestId: selectedCallerID,
          status: err?.response?.status || err?.status,
          error: err?.message || 'Unknown error',
          success: false,
          UUID,
          isEdit: false,
        });

        // eslint-disable-next-line no-console -- track errors in sentry if something unexpected goes wrong.
        console.error('Air Ambulance ETL Submit Error:', error);
      }
    },
    [
      isLoading,
      setIsLoading,
      dispatch,
      selectedCallerID,
      createTransport,
      UUID,
    ],
  );
}

/**
 * Transform form values to the flat APITransportRequest payload
 * expected by POST /api/gmr/transports.
 */
function transformFormToApiPayload(
  values: FormValuesWithSelect,
  selectedCallerID: string | null,
): EtlApiTransportRequest {
  const coords = parseCoordinates(values.pickup_coordinates);
  const isTest = values.launch_type === LAUNCH_TYPES.TEST;
  const isStandby = values.launch_type === LAUNCH_TYPES.STANDBY;

  return {
    isTest,
    isStandby,
    tripReason: getSelectValue(values.request_reason),
    comments: values.incident_info || undefined,
    callerId: selectedCallerID || undefined,
    multipleUnites: values.multiple_units_requested === 'Yes' || undefined,
    latitude: coords?.latitude ?? 0,
    longitude: coords?.longitude ?? 0,
    pickupLocationComments: values.pickup_details || undefined,
    otherResponders: values.air_service_responding === 'Yes',
    otherRespondersDeclinedWeather: values.air_service_declined === 'Yes',
    groundContact: values.ground_contact || undefined,
  } satisfies EtlApiTransportRequest;
}

export default useAirAmbulanceEtlSubmit;
